import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, AdminBody } from "./AdminHeader";
import MediaField, { GalleryField } from "./MediaField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search, Sparkles } from "lucide-react";

const emptyFromFields = (fields) => {
  const o = {};
  fields.forEach((f) => {
    o[f.name] = f.type === "multiselect" || f.type === "tags" || f.type === "gallery" ? [] : f.type === "checkbox" ? false : f.type === "number" ? 0 : f.default ?? "";
  });
  return o;
};

const FieldInput = ({ field, value, onChange, options }) => {
  const testid = `field-${field.name}`;
  switch (field.type) {
    case "textarea":
      return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 4} data-testid={testid} />;
    case "number":
      return <Input type="number" value={value ?? 0} onChange={(e) => onChange(parseInt(e.target.value || "0", 10))} data-testid={testid} />;
    case "checkbox":
      return <div className="flex items-center gap-2 pt-1"><Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} data-testid={testid} /><span className="text-sm text-f2b-gray">{field.checkboxLabel || "Oui"}</span></div>;
    case "image":
      return <MediaField value={value} onChange={onChange} testid={testid} />;
    case "gallery":
      return <GalleryField value={value} onChange={onChange} testid={testid} />;
    case "document":
      return <MediaField value={value} onChange={onChange} testid={testid} kind="document" />;
    case "select":
      return (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={testid}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
          <SelectContent>
            {(field.options || options?.[field.optionsKey] || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case "multiselect": {
      const opts = options?.[field.optionsKey] || [];
      const arr = value || [];
      return (
        <div className="flex flex-wrap gap-2 border border-f2b-filet p-3 max-h-44 overflow-y-auto" data-testid={testid}>
          {opts.length === 0 && <span className="text-xs text-f2b-gray">Aucune option</span>}
          {opts.map((o) => {
            const on = arr.includes(o.value);
            return (
              <button type="button" key={o.value} onClick={() => onChange(on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
                className={`text-xs px-3 py-1.5 border transition-colors ${on ? "bg-f2b-cyan text-f2b-black border-f2b-cyan" : "border-f2b-filet text-f2b-gray hover:border-f2b-cyan"}`}>
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case "tags": {
      const arr = value || [];
      return (
        <div>
          <Input placeholder="Ajouter puis Entrée" data-testid={testid}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                e.preventDefault();
                onChange([...arr, e.target.value.trim()]);
                e.target.value = "";
              }
            }} />
          <div className="flex flex-wrap gap-2 mt-2">
            {arr.map((t, i) => (
              <span key={i} className="text-xs bg-f2b-light border border-f2b-filet px-2 py-1 flex items-center gap-1">
                {t}<button type="button" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="text-f2b-gray hover:text-red-500">×</button>
              </span>
            ))}
          </div>
        </div>
      );
    }
    default:
      return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  }
};

const slugify = (t) =>
  (t || "")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();

const ResourceManager = ({ title, subtitle, endpoint, fields, columns, options, searchable = true, enableDocSync = false }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [slugTouched, setSlugTouched] = useState(false);
  const [q, setQ] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  const generateFromDoc = async () => {
    if (!form.google_doc_url) { toast.error("Renseignez d'abord le « Lien Google Doc source »."); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/articles/generate-from-doc", { google_doc_url: form.google_doc_url, title: form.title || "" });
      setForm((s) => ({ ...s, chapo: data.chapo, content: data.content, key_points: data.key_points || [] }));
      toast.success("Synthèse générée — relisez et modifiez avant d'enregistrer.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération impossible");
    } finally {
      setGenLoading(false);
    }
  };

  const mainFields = fields.filter((f) => f.section !== "advanced");
  const advancedFields = fields.filter((f) => f.section === "advanced");

  const handleChange = (f, v) => {
    setForm((s) => {
      const next = { ...s, [f.name]: v };
      if (f.autoSlug && !slugTouched) next[f.autoSlug] = slugify(v);
      return next;
    });
    if (f.slugField) setSlugTouched(true);
  };

  const { data: items = [], isLoading } = useQuery({
    queryKey: [`admin-${endpoint}`],
    queryFn: async () => (await api.get(`/admin/${endpoint}`)).data,
  });

  const save = useMutation({
    mutationFn: async (payload) => {
      if (editing) return (await api.put(`/admin/${endpoint}/${editing.id}`, payload)).data;
      return (await api.post(`/admin/${endpoint}`, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`admin-${endpoint}`] });
      toast.success(editing ? "Modifié" : "Créé");
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Erreur"),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/${endpoint}/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [`admin-${endpoint}`] }); toast.success("Supprimé"); },
  });

  const openCreate = () => { setEditing(null); setForm(emptyFromFields(fields)); setSlugTouched(false); setOpen(true); };
  const openEdit = (item) => {
    const o = emptyFromFields(fields);
    fields.forEach((f) => { if (item[f.name] !== undefined && item[f.name] !== null) o[f.name] = item[f.name]; });
    setEditing(item); setForm(o); setSlugTouched(true); setOpen(true);
  };

  const submit = (e) => {
    e.preventDefault();
    save.mutate(form);
  };

  const filtered = q
    ? items.filter((it) => JSON.stringify(it).toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <>
      <AdminHeader title={title} subtitle={subtitle}>
        <Button onClick={openCreate} data-testid={`admin-add-${endpoint}`} className="bg-f2b-cyan text-f2b-black hover:bg-f2b-cyan/90">
          <Plus size={16} className="mr-1" /> Ajouter
        </Button>
      </AdminHeader>
      <AdminBody>
        {searchable && (
          <div className="relative mb-5 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-f2b-gray" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9" data-testid="admin-search" />
          </div>
        )}
        <div className="bg-white border border-f2b-filet overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-f2b-light border-b border-f2b-filet">
              <tr>
                {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-medium text-f2b-gray">{c.label}</th>)}
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-f2b-gray">Chargement…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-f2b-gray">Aucun élément.</td></tr>
              ) : filtered.map((it) => (
                <tr key={it.id} className="border-b border-f2b-filet last:border-0 hover:bg-f2b-light/50" data-testid={`row-${endpoint}-${it.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-f2b-text align-middle">
                      {c.render ? c.render(it) : (
                        c.type === "image" ? (mediaUrl(it[c.key]) ? <img src={mediaUrl(it[c.key])} alt="" className="h-8 object-contain" /> : <span className="text-f2b-gray">—</span>)
                        : c.type === "badge" ? <Badge variant="secondary">{it[c.key]}</Badge>
                        : String(it[c.key] ?? "—").slice(0, 80)
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} className="text-f2b-gray hover:text-f2b-cyan p-1" data-testid={`edit-${it.id}`}><Pencil size={16} /></button>
                    <button onClick={() => { if (window.confirm("Supprimer cet élément ?")) del.mutate(it.id); }} className="text-f2b-gray hover:text-red-500 p-1 ml-1" data-testid={`delete-${it.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier" : "Ajouter"} — {title}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="resource-form">
            {mainFields.map((f) => (
              <div key={f.name}>
                <label className="block text-sm font-medium text-f2b-text mb-1.5">{f.label}{f.required && <span className="text-f2b-cyan"> *</span>}</label>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => handleChange(f, v)} options={options} />
                {f.hint && <p className="text-xs text-f2b-gray mt-1">{f.hint}</p>}
              </div>
            ))}
            {advancedFields.length > 0 && (
              <details className="border border-f2b-filet rounded-sm" data-testid="advanced-section">
                <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium text-f2b-gray hover:text-f2b-text">
                  Avancé (technique / synchronisation)
                </summary>
                <div className="px-4 pb-4 pt-1 space-y-4">
                  {advancedFields.map((f) => (
                    <div key={f.name}>
                      <label className="block text-sm font-medium text-f2b-text mb-1.5">{f.label}</label>
                      <FieldInput field={f} value={form[f.name]} onChange={(v) => handleChange(f, v)} options={options} />
                      {f.hint && <p className="text-xs text-f2b-gray mt-1">{f.hint}</p>}
                    </div>
                  ))}
                  {enableDocSync && (
                    <div className="pt-1">
                      <Button type="button" onClick={generateFromDoc} disabled={genLoading} data-testid="generate-from-doc-btn" className="bg-f2b-black text-white hover:bg-f2b-black/90">
                        <Sparkles size={16} className="mr-1.5" /> {genLoading ? "Génération…" : "Générer depuis le Google Doc"}
                      </Button>
                      <p className="text-xs text-f2b-gray mt-2">Lit le Google Doc (partagé en lecture), préremplit le chapô, le contenu (~200-300 mots) et 3 points clés. Le Google Doc reste la source. Rien n'est publié : relisez, modifiez puis cliquez sur Enregistrer.</p>
                    </div>
                  )}
                </div>
              </details>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={save.isPending} className="bg-f2b-cyan text-f2b-black hover:bg-f2b-cyan/90" data-testid="resource-save">
                {save.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResourceManager;
